import { asyncActionSuffixes } from './constants';
import { createReducer, ReducerMap, Reducer } from './createReducer';

export interface AsyncRequestReducerOptions<State extends FetchState> {
  asyncReducers?: {
    root?: Reducer<FetchState<State>>;
    start?: Reducer<FetchState<State>>;
    success?: Reducer<FetchState<State>>;
    failure?: Reducer<FetchState<State>>;
    reset?: Reducer<FetchState<State>>;
  };
  reducers?: ReducerMap<FetchState<State>>;
  initialStateProps?: State;
}

export type FetchState<S extends Partial<FetchState> = {}> = {
  isFetching: boolean;
  lastFetchAttempt: Date | null;
  error: any;
  response: S['response'];
  errorResponse?: any;
} & S;

const initialFetchState: FetchState<{}> = {
  isFetching: false,
  lastFetchAttempt: null,
  error: null,
  response: null,
  errorResponse: null,
};

function reduceFetchRoot<State extends FetchState>(
  reducer: Reducer<FetchState<State>>,
): Reducer<FetchState<State>> {
  return (state, action) => {
    return typeof reducer === 'function' ? reducer(state, action) : state;
  };
}

function reduceFetchStart<State extends FetchState>(
  reducer: Reducer<FetchState<State>>,
): Reducer<FetchState<State>> {
  return (state, action) => {
    const newState: FetchState<State> = {
      ...(state as any),
      isFetching: true,
      error: null,
    };
    return typeof reducer === 'function' ? reducer(newState, action) : newState;
  };
}

function reduceFetchSuccess<State extends FetchState>(
  reducer: Reducer<FetchState<State>>,
): Reducer<FetchState<State>> {
  return (state, action) => {
    const newState: FetchState<State> = {
      ...(state as any),
      isFetching: false,
      error: null,
      response: action.response,
      lastFetchAttempt: new Date(),
    };
    delete newState.errorResponse;
    return typeof reducer === 'function' ? reducer(newState, action) : newState;
  };
}

function reduceFetchError<State extends FetchState>(
  reducer: Reducer<FetchState<State>>,
): Reducer<FetchState<State>> {
  return (state, action) => {
    const newState: FetchState<State> = {
      ...(state as any),
      response: null,
      isFetching: false,
      error: action.error,
      errorResponse: action.response,
      lastFetchAttempt: new Date(),
    };
    return typeof reducer === 'function' ? reducer(newState, action) : newState;
  };
}

function reduceFetchReset<State extends FetchState>(
  reducer: Reducer<FetchState<State>>,
): Reducer<FetchState<State>> {
  return (state, action) => {
    const newState = {
      ...state,
      ...initialFetchState,
    };
    return typeof reducer === 'function' ? reducer(newState, action) : newState;
  };
}

function createAsyncRequestReducer<State extends FetchState>(
  baseActionType: string,
  options: AsyncRequestReducerOptions<State> = {},
) {
  const { reducers = {}, asyncReducers = {}, initialStateProps = {} } = options;
  const handlers: ReducerMap<FetchState<State>> = {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    [baseActionType]: reduceFetchRoot(asyncReducers.root),
    [baseActionType + asyncActionSuffixes.start]: reduceFetchStart(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      asyncReducers.start,
    ),
    [baseActionType + asyncActionSuffixes.success]: reduceFetchSuccess(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      asyncReducers.success,
    ),
    [baseActionType + asyncActionSuffixes.failure]: reduceFetchError(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      asyncReducers.failure,
    ),
    [baseActionType + asyncActionSuffixes.reset]: reduceFetchReset(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      asyncReducers.reset,
    ),
    ...reducers,
  };

  return createReducer(
    createInitialState<FetchState<State>>(initialStateProps),
    handlers,
  );
}

function createInitialState<S extends FetchState>(
  props: any = {},
): FetchState<S> {
  return {
    ...initialFetchState,
    ...props,
  };
}

export {
  initialFetchState,
  reduceFetchRoot,
  reduceFetchStart,
  reduceFetchSuccess,
  reduceFetchError,
  reduceFetchReset,
  createAsyncRequestReducer,
  createInitialState,
};
